import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AGE_RANGES = [
  { value: "<18", label: "Menos de 18" },
  { value: "18-24", label: "18 – 24" },
  { value: "25-34", label: "25 – 34" },
  { value: "35-44", label: "35 – 44" },
  { value: "45+", label: "45+" },
];

const INTEREST_OPTIONS = [
  { value: "redes_sociais", label: "Redes sociais" },
  { value: "eventos", label: "Eventos / flashmobs" },
  { value: "cliente_oculto", label: "Cliente oculto" },
  { value: "pesquisas", label: "Pesquisas" },
  { value: "ativacoes_loja", label: "Ativações em loja" },
  { value: "politica", label: "Campanhas políticas" },
];

const INITIAL = {
  name: "",
  email: "",
  phone: "",
  city: "",
  state: "",
  age_range: "",
  socials: "",
  interests: [],
  in_person: null,
  referred_by: "",
  message: "",
};

export default function AgentLeadForm() {
  const [form, setForm] = useState(INITIAL);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const toggleInterest = (v) => {
    setForm((f) => ({
      ...f,
      interests: f.interests.includes(v)
        ? f.interests.filter((x) => x !== v)
        : [...f.interests, v],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (!form.name || !form.email) {
      toast.error("Preencha nome e e-mail.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/agent-leads`, {
        ...form,
        phone: form.phone || null,
        city: form.city || null,
        state: form.state || null,
        age_range: form.age_range || null,
        socials: form.socials || null,
        referred_by: form.referred_by || null,
        message: form.message || null,
        in_person: form.in_person,
      });
      setDone(true);
      toast.success("Cadastro recebido. Avisaremos quando abrir sua vaga.");
      setForm(INITIAL);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "Não foi possível enviar agora. Tente novamente em instantes.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="cadastro"
      data-testid="agent-form-section"
      className="relative py-24 sm:py-32 bg-[#1B1D1D] text-[#F5FAFB] overflow-hidden"
    >
      <div
        className="absolute -top-32 -left-20 h-[460px] w-[460px] rounded-full blur-3xl opacity-25"
        style={{
          background: "radial-gradient(closest-side, #7E20FF, transparent 70%)",
        }}
      />
      <div
        className="absolute -bottom-32 -right-20 h-[400px] w-[400px] rounded-full blur-3xl opacity-20"
        style={{
          background: "radial-gradient(closest-side, #208BFF, transparent 70%)",
        }}
      />

      <div className="relative mx-auto max-w-7xl px-6 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-5">
          <span className="text-xs font-semibold tracking-[0.22em] uppercase text-[#7E20FF]">
            Cadastro · gratuito
          </span>
          <h2
            data-testid="agent-form-headline"
            className="mt-4 font-display text-4xl sm:text-5xl font-bold tracking-tighter leading-[0.96] text-[#F5FAFB]"
          >
            Quero fazer parte da RSM Network.
          </h2>
          <p className="mt-6 text-base sm:text-lg leading-relaxed text-[#F5FAFB]/70 max-w-md">
            Cadastro gratuito. Seleção e missões de acordo com o seu perfil,
            cidade e disponibilidade. Você só aceita o que faz sentido.
          </p>

          <div className="mt-10 space-y-4 text-sm text-[#F5FAFB]/75 max-w-sm">
            {[
              "Pagamentos transparentes por missão concluída.",
              "Aprendizado em marketing e redes na prática.",
              "Sem promessas exageradas de renda fácil.",
            ].map((t, i) => (
              <div key={t} className="flex items-start gap-3">
                <CheckCircle2
                  size={18}
                  className="mt-0.5 text-[#7E20FF] flex-shrink-0"
                />
                <span>{t}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-7">
          {done ? (
            <div
              data-testid="agent-form-success"
              className="rounded-2xl border border-[#7E20FF]/30 bg-white/5 backdrop-blur p-10 text-center"
            >
              <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-[#7E20FF]/20 text-[#7E20FF]">
                <CheckCircle2 size={28} />
              </div>
              <h3 className="mt-6 font-display text-2xl font-semibold text-[#F5FAFB]">
                Cadastro recebido.
              </h3>
              <p className="mt-3 text-[#F5FAFB]/70 max-w-md mx-auto">
                Avisaremos por e-mail quando abrir uma vaga compatível com o seu
                perfil. Enquanto isso, vale convidar amigos para fortalecer o
                seu time.
              </p>
              <button
                data-testid="agent-form-reset"
                onClick={() => setDone(false)}
                className="mt-8 inline-flex items-center gap-2 rounded-full border border-white/15 px-6 py-3 text-sm font-medium text-[#F5FAFB]/85 hover:border-[#7E20FF] hover:text-[#7E20FF] transition-colors"
              >
                Cadastrar outra pessoa
              </button>
            </div>
          ) : (
            <form
              onSubmit={submit}
              data-testid="agent-form"
              className="rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur p-6 sm:p-8"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Seu nome *">
                  <input
                    data-testid="agent-input-name"
                    required
                    value={form.name}
                    onChange={set("name")}
                    placeholder="Como podemos te chamar"
                    className={inputCls}
                  />
                </Field>
                <Field label="E-mail *">
                  <input
                    data-testid="agent-input-email"
                    type="email"
                    required
                    value={form.email}
                    onChange={set("email")}
                    placeholder="seu@email.com"
                    className={inputCls}
                  />
                </Field>
                <Field label="Telefone / WhatsApp">
                  <input
                    data-testid="agent-input-phone"
                    value={form.phone}
                    onChange={set("phone")}
                    placeholder="(11) 90000-0000"
                    className={inputCls}
                  />
                </Field>
                <Field label="Faixa etária">
                  <select
                    data-testid="agent-input-age"
                    value={form.age_range}
                    onChange={set("age_range")}
                    className={inputCls}
                  >
                    <option value="">Selecione (opcional)…</option>
                    {AGE_RANGES.map((a) => (
                      <option key={a.value} value={a.value}>
                        {a.label}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Cidade">
                  <input
                    data-testid="agent-input-city"
                    value={form.city}
                    onChange={set("city")}
                    placeholder="Ex.: São Paulo"
                    className={inputCls}
                  />
                </Field>
                <Field label="Estado">
                  <input
                    data-testid="agent-input-state"
                    value={form.state}
                    onChange={set("state")}
                    placeholder="UF · ex.: SP"
                    className={inputCls}
                  />
                </Field>
                <Field label="Suas redes principais" className="sm:col-span-2">
                  <input
                    data-testid="agent-input-socials"
                    value={form.socials}
                    onChange={set("socials")}
                    placeholder="@instagram, @tiktok, link do perfil…"
                    className={inputCls}
                  />
                </Field>
              </div>

              <div className="mt-5">
                <span className="text-[11px] font-semibold tracking-[0.14em] uppercase text-[#F5FAFB]/55">
                  Que tipos de missão te interessam?
                </span>
                <div
                  data-testid="agent-input-interests"
                  className="mt-3 flex flex-wrap gap-2"
                >
                  {INTEREST_OPTIONS.map((opt) => {
                    const active = form.interests.includes(opt.value);
                    return (
                      <button
                        type="button"
                        key={opt.value}
                        data-testid={`agent-interest-${opt.value}`}
                        onClick={() => toggleInterest(opt.value)}
                        className={`rounded-full px-3.5 py-1.5 text-xs font-semibold tracking-wide uppercase transition-all duration-200 border ${
                          active
                            ? "bg-[#7E20FF] text-white border-[#7E20FF]"
                            : "bg-transparent text-[#F5FAFB]/70 border-white/15 hover:border-[#7E20FF]/60 hover:text-[#F5FAFB]"
                        }`}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Topa missões presenciais?">
                  <div className="flex gap-2">
                    {[
                      { v: true, label: "Sim" },
                      { v: false, label: "Não" },
                    ].map((opt) => {
                      const active = form.in_person === opt.v;
                      return (
                        <button
                          type="button"
                          key={String(opt.v)}
                          data-testid={`agent-in-person-${opt.label.toLowerCase()}`}
                          onClick={() =>
                            setForm({ ...form, in_person: opt.v })
                          }
                          className={`flex-1 rounded-xl border px-4 py-3 text-sm font-medium transition-colors ${
                            active
                              ? "bg-[#7E20FF] text-white border-[#7E20FF]"
                              : "bg-transparent text-[#F5FAFB]/75 border-white/12 hover:border-[#7E20FF]/60"
                          }`}
                        >
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                </Field>
                <Field label="Foi indicado(a) por alguém?">
                  <input
                    data-testid="agent-input-referrer"
                    value={form.referred_by}
                    onChange={set("referred_by")}
                    placeholder="Nome ou @ de quem te indicou"
                    className={inputCls}
                  />
                </Field>
              </div>

              <Field label="Quer contar mais alguma coisa?" className="mt-5">
                <textarea
                  data-testid="agent-input-message"
                  value={form.message}
                  onChange={set("message")}
                  rows={3}
                  placeholder="Disponibilidade, experiência anterior, expectativas…"
                  className={`${inputCls} resize-none`}
                />
              </Field>

              <button
                type="submit"
                disabled={loading}
                data-testid="agent-form-submit"
                className="mt-6 inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-full bg-[#7E20FF] px-7 py-4 text-base font-semibold text-white shadow-sm transition-all duration-300 ease-out hover:bg-[#6818D6] hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0 focus:outline-none focus:ring-2 focus:ring-[#7E20FF] focus:ring-offset-2 focus:ring-offset-[#1B1D1D]"
              >
                {loading ? (
                  <>
                    <Loader2 size={18} className="animate-spin" /> Enviando…
                  </>
                ) : (
                  <>
                    Quero ser agente RSM
                    <ArrowRight size={18} />
                  </>
                )}
              </button>

              <p className="mt-4 text-xs text-[#F5FAFB]/55">
                Cadastro gratuito. Seleção e missões dependem do seu perfil,
                cidade e demanda. Seus dados não são compartilhados.
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="text-[11px] font-semibold tracking-[0.14em] uppercase text-[#F5FAFB]/55">
        {label}
      </span>
      <div className="mt-2">{children}</div>
    </label>
  );
}

const inputCls =
  "w-full rounded-xl border border-white/12 bg-[#0e0f0f] px-4 py-3 text-sm text-[#F5FAFB] placeholder:text-[#F5FAFB]/30 focus:border-[#7E20FF] focus:outline-none focus:ring-2 focus:ring-[#7E20FF]/30 transition-all";
